import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Text, Button, Popper, MetricCard } from "@fragments";
import {
  IconUsersGroup,
  IconUserPlus,
  IconPhoto,
  IconDots,
  IconSettings,
  IconTrash,
  IconUpload,
  IconUsers,
  IconUserCheck,
  IconCoin,
  IconArrowRight,
  IconWorld,
  IconGeometry,
  IconChevronDown,
  IconPlus,
  IconSchool,
} from "@tabler/icons-react";

import { DURATION_UNIT_LABEL } from "../enums/durationUnits";

import peopleCircle from "./assets/people-circle.png";

/**
 * Programs list — §6.1. Cards show the metrics strip, a ⋯ options menu, a
 * status select (Publicado/Rascunho) and delete. Create/edit open the editor
 * modal (handled by the controller). Invite/import are stage 4 (toasts).
 */
export default function MentoringProgramsView({
  programs,
  loading,
  creatorMentorings = [],
  onCreateProgram,
  onEditProgram,
  onSetActive,
  onDeleteProgram,
  onInvite,
  onImport,
}) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const popperRef = useRef();
  const statusPopperRef = useRef();

  function manageProgram(program) {
    navigate(`/${slug}/settings/mentoring/${program.id}`);
  }

  function openMenu(event, program) {
    event.stopPropagation();
    popperRef.current.open(
      [
        {
          id: "invite",
          label: "Convidar",
          icon: <IconUserPlus size={15} />,
          onClick: async () => onInvite?.(program),
        },
        {
          id: "import",
          label: "Importar mentorados",
          icon: <IconUpload size={15} />,
          onClick: async () => onImport?.(program),
        },
        {
          id: "manage",
          label: "Gerenciar mentorados",
          icon: <IconUsersGroup size={15} />,
          onClick: async () => manageProgram(program),
        },
        {
          id: "edit",
          label: "Configurar programa",
          icon: <IconSettings size={15} />,
          onClick: async () => onEditProgram?.(program),
        },
        { divider: true, id: "divider-1" },
        {
          id: "delete",
          label: "Excluir programa",
          icon: <IconTrash size={15} />,
          variant: "danger",
          onClick: async () => confirmDelete(program),
        },
      ],
      "bottom-end",
      event.currentTarget,
    );
  }

  function openStatusMenu(event, program) {
    event.stopPropagation();
    statusPopperRef.current.open(
      [
        {
          id: "published",
          label: "Publicado",
          icon: <IconWorld size={15} />,
          active: program.active,
          onClick: async () => onSetActive?.(program, true),
        },
        {
          id: "draft",
          label: "Rascunho",
          icon: <IconGeometry size={15} />,
          active: !program.active,
          onClick: async () => onSetActive?.(program, false),
        },
      ],
      "bottom-end",
      event.currentTarget,
    );
  }

  function confirmDelete(program) {
    window.openConfirm?.({
      title: "Excluir programa",
      headline: "Atenção!",
      subtitle:
        "Inscrições ativas serão encerradas (os repasses são removidos das comunidades dos criadores, que serão notificados). Esta ação é permanente.",
      buttons: [
        {
          text: "Excluir permanentemente",
          onClick: () => onDeleteProgram?.(program),
          variant: "contained",
          color: "error",
        },
        { text: "Cancelar", variant: "text" },
      ],
    });
  }

  // Empty state (§6.1): the header stacks — title/subtitle centered, the
  // create card (column layout) right below — and the list area renders nothing.
  const isEmpty = !loading && programs.length === 0;

  return (
    <div className={`mentoring-tab px-4${isEmpty ? " is-empty" : ""}`}>
      <div className="mt-4 mentoring-tab-header">
        <div className="mentoring-tab-intro">
          <Text.h2 left={<IconUsersGroup />} weight={500} className="mb-1">
            Programas de mentoria
          </Text.h2>
          <Text.span
            contrast={6}
            style={isEmpty ? undefined : { paddingLeft: "44px" }}
          >
            Crie programas, convide criadores e acompanhe os resultados dos seus
            mentorados.
          </Text.span>
        </div>

        <div className="mentoring-header-aside">
          {creatorMentorings.map((mentoring) => (
            <CreatorMentoringCard
              key={mentoring.id}
              mentoring={mentoring}
              onOpen={() => navigate(`/mentoring/${mentoring.id}`)}
            />
          ))}

          <div className="mentoring-create-card">
            <img
              src={peopleCircle}
              alt=""
              className="mcc-illustration"
              loading="lazy"
            />
            <div className="mcc-text">
              <Text.h5 contrast={9} weight={500}>
                Crie um programa de mentoria
              </Text.h5>
              <Text.span contrast={6}>
                Estruture sua mentoria, convide outros criadores e receba
                participação da receita de todo o seu ecossistema
              </Text.span>
              <Button
                text="Criar programa"
                endIcon={<IconPlus />}
                color="inverse"
                highlightColor="current"
                variant="rounded"
                size="small"
                className="mcc-cta"
                onClick={onCreateProgram}
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mentoring-programs-list">
          {[0, 1].map((i) => (
            <ProgramCardSkeleton key={i} index={i} />
          ))}
        </div>
      ) : programs.length > 0 ? (
        <div className="mentoring-programs-list">
          {programs.map((program, index) => (
            <ProgramCard
              key={program.id}
              program={program}
              index={index}
              onOpenMenu={openMenu}
              onOpenStatus={openStatusMenu}
              onManage={manageProgram}
            />
          ))}
        </div>
      ) : null}

      <Popper controlRef={popperRef} config={{ activateOnHover: true }} />
      <Popper controlRef={statusPopperRef} config={{ activateOnHover: true }} />
    </div>
  );
}

// Shortcut card shown above the create-program card when THIS community is
// itself being mentored in another owner's program. Opens the creator-side
// enrollment detail (§6.11).
function CreatorMentoringCard({ mentoring, onOpen }) {
  const banner = mentoring.program?.banner?.url ?? null;
  const name = mentoring.program?.name || "Mentoria";

  function handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  }

  return (
    <div
      className="mentoring-creator-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
    >
      <div className="mcr-banner">
        {banner ? (
          <img src={banner} alt={name} loading="lazy" />
        ) : (
          <span className="mcr-banner-fallback">
            <IconSchool size={20} />
          </span>
        )}
      </div>

      <div className="mcr-text">
        <Text.small contrast={6}>Você é mentorado em</Text.small>
        <Text.h5 contrast={9} weight={500} className="mcr-name">
          {name}
        </Text.h5>
      </div>

      <IconArrowRight size={18} className="mcr-arrow" />
    </div>
  );
}

function ProgramCard({ program, index, onOpenMenu, onOpenStatus, onManage }) {
  const metrics = program.metrics ?? {};
  const bodyRef = useRef(null);
  const [bannerWidth, setBannerWidth] = useState(null);

  // The banner fills the card height (= body height); derive its 4:3 width from
  // that height. Pure CSS can't transfer a sibling-driven height → width here.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const next = Math.round(el.offsetHeight * (4 / 3));
      setBannerWidth((prev) =>
        prev != null && Math.abs(prev - next) <= 1 ? prev : next,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="mentoring-program-card" style={{ "--i": index }}>
      {program.banner?.url ? (
        <img
          src={program.banner.url}
          alt={program.name}
          className="banner-mentoring-effect"
          loading="lazy"
        />
      ) : null}
      <div
        className="mpc-banner"
        style={bannerWidth ? { width: `${bannerWidth}px` } : undefined}
        onClick={() => onManage(program)}
        role="button"
        tabIndex={0}
      >
        {program.banner?.url ? (
          <img src={program.banner.url} alt={program.name} loading="lazy" />
        ) : (
          <div className="mpc-banner-fallback">
            <IconPhoto size={22} />
          </div>
        )}
      </div>

      <div className="mpc-body" ref={bodyRef}>
        <div className="mpc-head">
          <div style={{ width: "100%", flexGrow: 1 }}>
            <Text.h3 contrast={9} weight={500} grow>
              {program.name}
            </Text.h3>
            <Text.span contrast={5} className="mpc-terms">
              {formatProgramTerms(program)}
            </Text.span>
          </div>

          <Button
            size="small"
            color={"overlay"}
            startIcon={
              program.active ? (
                <IconWorld size={15} />
              ) : (
                <IconGeometry size={15} />
              )
            }
            endIcon={<IconChevronDown size={14} />}
            text={program.active ? "Publicado" : "Rascunho"}
            onMouseEnter={(e) => onOpenStatus(e, program)}
            onClick={(e) => onOpenStatus(e, program)}
            preventLoading
          />

          <Button
            size="medium"
            color="white"
            endIcon={<IconArrowRight />}
            highlightColor="black"
            variant="rounded"
            onClick={() => onManage(program)}
          >
            Gerenciar mentorados
          </Button>
          <Button
            size="small"
            color="text"
            icon={<IconDots size={16} />}
            onMouseEnter={(e) => onOpenMenu(e, program)}
            onClick={(e) => onOpenMenu(e, program)}
          />
        </div>

        <div className="mpc-metrics">
          <MetricCard
            title="Inscritos"
            leftIcon={<IconUsers size={15} />}
            value={program.enrollments_count}
            variant="number"
          />
          <MetricCard
            title="Ativos"
            leftIcon={<IconUserCheck size={15} />}
            value={metrics.active_enrollments}
            variant="number"
          />
          <MetricCard
            title="Membros alcançados"
            leftIcon={<IconUsersGroup size={15} />}
            value={metrics.total_members}
            variant="number"
          />
          <MetricCard
            title="Receita gerada"
            leftIcon={<IconCoin size={15} />}
            value={
              metrics.total_revenue_cents != null
                ? metrics.total_revenue_cents / 100
                : undefined
            }
            variant="money"
          />
        </div>
      </div>
    </div>
  );
}

function ProgramCardSkeleton({ index }) {
  return (
    <div
      className="mentoring-program-card is-skeleton"
      style={{ "--i": index }}
    >
      <div className="mpc-banner skel" />
      <div className="mpc-body">
        <div className="skel-line skel" style={{ width: "45%" }} />
        <div className="skel-line skel" style={{ width: "70%" }} />
        <div className="mpc-metrics">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="skel"
              style={{ height: 64, borderRadius: "var(--gap-2)" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// "20% de coprodução durante 6 meses · mentoria de 12 semanas" — see §6.1.
function formatProgramTerms(program) {
  const parts = [];

  if (program.split_percentage != null) {
    const window = program.split_duration_value
      ? `${program.split_duration_value} ${DURATION_UNIT_LABEL[program.split_duration_unit] ?? ""}`.trim()
      : "o período da mentoria";
    parts.push(`${program.split_percentage}% de coprodução durante ${window}`);
  }

  parts.push(
    program.duration_value
      ? `mentoria de ${program.duration_value} ${DURATION_UNIT_LABEL[program.duration_unit] ?? ""}`.trim()
      : "mentoria indefinida",
  );

  return parts.join(" · ");
}
